import { Object3D, Box3, Line, Sphere, Vector3 } from 'three';
import type {
  BufferGeometry,
  DepthModes,
  Float32BufferAttribute,
  Group,
  Intersection,
  Matrix4,
  Raycaster,
} from 'three';

import { noop } from '@frontend/utils';

// eslint-disable-next-line no-restricted-imports
import type { ColorRGBArray, ColorRGBAArray } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import type { Intersectable } from '../../renderer/renderer';
import { fillRepeatedPattern } from '../step/utils';
import { type STEPBody, defaultEdgeColorComponents } from './STEPBody';
import type { EdgeMetaData } from './STEPGeometry';
import type { Style } from './StepStyleAdapter';
import { StepStyleAdapter } from './StepStyleAdapter';
import type { ActiveMetaData } from './utils';
import { createActiveMetaData } from './utils';

//PartialEdge: StepEdgeModel's edge is broken up into chunks because of the max number of positions that can be in mesh
//* The geometry in StepEdgeModel is actually in a packed set of BufferGeometry in the host. The vast majority of time, the
//  geometry for a single face is in a single BufferGeometry, but sometimes it staddles 2 or more BufferGeometry.
//  The StepEdgeModel keeps track of the PartialEdges that are straddled across 1 or more BufferGeometry
class PartialEdge extends Object3D {
  public parent: StepEdgeModel;
  public readonly isLineSegments = false; //See Line.prototype.raycast implementation (must be false to iterate over drawRange as expected)

  constructor(
    public geometry: BufferGeometry, //a proxy of a small range in one of the parent body's edge buffers
    parent: StepEdgeModel,
    public readonly bufNum: number,
  ) {
    super();
    this.parent = parent;

    //this.visible is deleted so we can use getter/setter (which is on prototype)
    //Note: There is a difference between this.visible and this.prototype.visible getter/setter
    //Similarly, do the same for this.matrixWorld
    const visible = this.parent.visible;
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    //@ts-ignore
    delete this.visible; //delete this.visible property created by super() so getter/setter can be used
    this.parent.visible = visible;
    //@ts-ignore
    delete this.matrixWorld; //delete this.matrixWorld property created by super() so getter/setter can be used
    /* eslint-enable @typescript-eslint/ban-ts-comment */
  }

  public *positions() {
    const { start, count } = this.geometry.drawRange;
    const end = start + count * 3;
    const parentGeometry = this.parent.host.faceGeometries[this.bufNum];
    const parentPositions = (
      parentGeometry.attributes.position as Float32BufferAttribute
    )?.array as Float32Array;
    for (let i = start; i < end; i++) {
      yield parentPositions[i];
    }
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public get matrixWorld(): Matrix4 {
    return this.parent.matrixWorld;
  }

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public set matrixWorld(v: Matrix4) {
    if (this.parent) {
      this.parent.matrixWorld = v;
    }
  }

  //Note: this.visible is deleted after super() is called. It will be replaced by this getter/setter pair
  //@ts-ignore
  public get visible(): boolean {
    return this.parent.visible;
  }

  //@ts-ignore
  public set visible(v: boolean) {
    if (this.parent) {
      this.parent.visible = v;
    }
  }

  /* eslint-enable @typescript-eslint/ban-ts-comment */

  //@remarks: Prefer not to use this.material (unlike PartialFace, this.material is not required by this.raycast)
  // public get material() {
  //   return this.parent.host.faceMaterial;
  // }

  public raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    //* raycast is delegated to Line's implementation
    //* Note: this.geometry is a Proxy to the parentGeometry with the boundBox, boundSphere and drawRange set based on this edge's positions
    // in the parentGeometry. This way the line's raycast only looks at the positions that are in range for this edge, and so that it considers
    // the appropriate boundBox and boundSphere for this edge.
    Line.prototype.raycast.call(this, raycaster, intersects);
  }
}

const _sphere = new Sphere(); //temp sphere reused for intersection tests

/*
 * StepEdgeModel provides some metadata about the edge
 * @remarks StepEdgeModel extends Object3D and overrides the raycast() method so it can be used to calculate this edge's intersection by a Raycaster
 */
export class StepEdgeModel extends Object3D implements Intersectable {
  /**
   * @remarks: boundBox is transformed in context of its body, whereas this.geometry.boundBox is NOT transformed!
   * this.boundBox is used by intersection-utils.ts to test if frustrum intersects this box
   */
  public readonly boundBox: Box3;
  public readonly boundSphere: Sphere;
  public readonly partialEdges: PartialEdge[] = [];
  public readonly parent: Group; //The parent group for the body's edges this edge belongs to
  public readonly activeMetaData: ActiveMetaData;
  public readonly metaData: Omit<
    EdgeMetaData,
    'boundBox' | 'boundSphere' | 'bufferRanges'
  >;
  private _style?: Style;

  constructor(
    public readonly name: string,
    _metaData: EdgeMetaData,
    public readonly host: STEPBody,
  ) {
    super();
    const { boundBox, boundSphere, bufferRanges, ...metaData } = _metaData;
    this.metaData = metaData; //boundBox and boundSphere removed from metaData
    this.activeMetaData = createActiveMetaData(metaData, host.transform);

    //cloned because this.boundBox will be transformed in context of its body
    //do not confuse this with this.geometry.boundBox which is not transformed
    this.boundBox = new Box3(
      new Vector3(...boundBox.min),
      new Vector3(...boundBox.max),
    );
    this.boundSphere = new Sphere(
      new Vector3(...boundSphere.center),
      boundSphere.radius,
    );

    this.parent = this.host.edgesGroup;

    //this.visible is deleted so we can use getter/setter (which is on prototype)
    const visible = this.parent.visible;
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    //@ts-ignore
    delete this.visible; //delete this.visible property created by super() so getter/setter can be used
    this.parent.visible = visible;
    //@ts-ignore
    delete this.matrixWorld; //delete this.visible property created by super() so getter/setter can be used
    /* eslint-enable @typescript-eslint/ban-ts-comment */

    //this.style is an adapter view into metaData
    this._style = new StepStyleAdapter(this, this.host);

    for (const {
      bufNum,
      bufStart,
      bufCount,
      boundBox,
      boundSphere,
    } of bufferRanges) {
      const drawRange: Readonly<BufferGeometry['drawRange']> = {
        start: bufStart / 3,
        count: bufCount / 3,
      };
      const _boundBox = new Box3(
        new Vector3(...boundBox.min),
        new Vector3(...boundBox.max),
      );
      const _boundSphere = new Sphere(
        new Vector3(...boundSphere.center),
        boundSphere.radius,
      );

      //Note:
      //* It is only intended to use this.geometry for this.raycast().
      //* It is preferred to not use this.geometry elsewhere because care would be required to ensure the correct index in
      //  the parentGeometry is used. this.raycast is fine with this Proxy because it's implementation uses drawRange
      //  * As an example, caliper-utils.ts used to access `geometry.attributes.position` to construct a vector for the first position
      //    `this.firstPosition` is preferred because it takes care of using the correct index
      //  * Another example in caliper-utils.ts is getting the `this.firstNormal` which also takes care to use the correct index
      //* geometry is a Proxy to the parentGeometry of the edge and has the drawRange, boundBox and boundSphere specified/precomputed
      //  for this edge.  And the index set to null.  The index is not necessary because the positions are always sequential for the drawRange.
      //  (By returning `null`, the indexing of the position attribute over the drawRange is a bit more efficient)
      //* A Proxy was chosen so the parentGeometry remains untouched. Currently threejs doesn't observe properties on BufferGeometry, and this.raycast() is synchronous
      //  so it is possible to temporarily swap out these values on the parentGeometry and restore them. But I want to avoid the possibility of WebGL seeing changes to the
      //  parentGeometry because it is in the scene.
      //* This proxied geometry is only used by raycast() to calculate the intersection of a ray with this StepEdgeModel
      const geometry = new Proxy(this.host.edgeGeometries[bufNum], {
        get(target, prop, receiver) {
          switch (prop) {
            case 'drawRange':
              return drawRange;
            case 'boundingBox':
              return _boundBox;
            case 'boundingSphere':
              return _boundSphere;
            case 'index':
              return null;
            case 'computeBoundingSphere':
            case 'computeBoundingBox':
            case 'computeTangents':
            case 'computeVertexNormals':
              return noop;
            default:
              return Reflect.get(target, prop, receiver);
          }
        },
      });

      this.partialEdges.push(new PartialEdge(geometry, this, bufNum));
    }
  }

  public dispose() {
    if (!this._style) {
      return; //already disposed
    }
    delete this._style;
    this.partialEdges.length = 0;
  }

  public get style(): Style {
    if (!this._style) {
      throw new Error('This object was disposed');
    }
    return this._style;
  }

  public get rootBodyOrAssemblyInstanceId() {
    return this.host.body.rootBodyOrAssemblyInstanceId;
  }

  //@remarks: Prefer not to use this.material (unlike StepFaceModel, this.material is not required by this.raycast)
  //public get material() {
  //  return this.host.edgeMaterial;
  //}

  //@remarks: This is actually just the first geometry
  //It is defined to satisify `Intersectable` but not actually required because raycaster delegates to each `PartialEdge`
  public get geometry() {
    return this.partialEdges[0].geometry as BufferGeometry; //actually just the first geometry
  }

  public get firstPosition(): Vector3 {
    const firstGeometry = this.partialEdges[0].geometry;
    return new Vector3().fromBufferAttribute(
      firstGeometry.attributes.position as Float32BufferAttribute,
      firstGeometry.drawRange.start,
    );
  }

  //@remarks: this.index is used by getHighlightForHover
  public get index(): number {
    return this.metaData.index;
  }

  public get partUuid() {
    return this.host.partUuid;
  }

  public get parentName() {
    return this.host.name;
  }

  public get isMaterial(): boolean | undefined {
    return this.host.isMaterial;
  }

  public get transform() {
    return this.host.transform;
  }

  public get body() {
    return this.host.body;
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public get matrixWorld(): Matrix4 {
    return this.parent.matrixWorld;
  }

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public set matrixWorld(v: Matrix4) {
    if (this.parent) {
      this.parent.matrixWorld = v;
    }
  }

  //Note: this.visible is deleted after super() is called. It will be replaced by this getter/setter pair
  //@ts-ignore
  public get visible(): boolean {
    //Edge's visibility is controlled by the parent's visibility and the opacity
    return this.parent.visible && this.opacity > 0;
  }

  //@ts-ignore
  public set visible(v: boolean) {
    //Edge's visibility is controlled by the parent's visibility and the opacity
    //But when using the setter, we only modify the parent's visibility (which impacts ALL sibling edges)
    if (this.parent) {
      this.parent.visible = v;
    }
  }

  /* eslint-enable @typescript-eslint/ban-ts-comment */

  public get depthFunc(): DepthModes {
    return this.host.edgeMaterial.depthFunc;
  }

  public set depthFunc(v: DepthModes) {
    this.host.edgeMaterial.depthFunc = v;
  }

  public get opacity(): number {
    const firstGeometry = this.partialEdges[0].geometry;
    const colors = (firstGeometry.attributes.color as Float32BufferAttribute)
      ?.array as Float32Array;
    //All lines in a edge will have the same opacity, so arbitrarily using the first
    return colors[firstGeometry.drawRange.start * 4 + 3];
  }

  public setOpacity(v: number, update = true) {
    for (const partialEdge of this.partialEdges) {
      const colors = (
        partialEdge.geometry.attributes.color as Float32BufferAttribute
      )?.array as Float32Array;
      const start = partialEdge.geometry.drawRange.start * 4;
      this.setColorAndOpacity(
        [colors[start], colors[start + 1], colors[start + 2], v],
        update,
      );
    }
  }

  public get linewidth(): number {
    //Currently an edge's lineWidth is controlled by its parent edgeMaterial (all edges are visible or all hidden)
    return this.host.edgeMaterial.linewidth;
  }

  public set linewidth(v: number) {
    //Currently an edge's lineWidth is controlled by its parent edgeMaterial (all edges are visible or all hidden)
    this.host.edgeMaterial.linewidth = v;
  }

  public get defaultColor(): ColorRGBArray {
    return defaultEdgeColorComponents;
  }

  //color rgb components in range of 0..1
  public get color(): ColorRGBArray {
    const firstGeometry = this.partialEdges[0].geometry;
    const colors = (firstGeometry.attributes.color as Float32BufferAttribute)
      ?.array as Float32Array;
    const start = firstGeometry.drawRange.start * 4;
    //return colors.slice(start, start + 3) as unknown as ColorRGBArray;
    return [colors[start], colors[start + 1], colors[start + 2]];
  }

  public get colorAndOpacity(): ColorRGBAArray {
    const firstGeometry = this.partialEdges[0].geometry;
    const colors = (firstGeometry.attributes.color as Float32BufferAttribute)
      ?.array as Float32Array;
    const start = firstGeometry.drawRange.start * 4;
    //Type is techincally Float32Array, but treating as tuple
    //return colors.slice(start, start + 4) as unknown as ColorRGBAArray;
    return [
      colors[start],
      colors[start + 1],
      colors[start + 2],
      colors[start + 3],
    ];
  }

  public setColorAndOpacity(
    edgeColorAndOpacity: ColorRGBAArray,
    update = true,
  ) {
    for (const partialEdge of this.partialEdges) {
      const colorAttribute = partialEdge.geometry.attributes
        .color as Float32BufferAttribute;
      const colors = colorAttribute?.array as Float32Array;
      if (colors) {
        const { drawRange } = partialEdge.geometry;
        const start = drawRange.start * 4; //drawRange.start is the edge's start (not positions start)
        const count = drawRange.count;
        let needsUpdate = false;
        //Check red, green, blue and alpha values to see if an update is required
        if (
          edgeColorAndOpacity[0] !== colors[start] ||
          edgeColorAndOpacity[1] !== colors[start + 1] ||
          edgeColorAndOpacity[2] !== colors[start + 2] ||
          edgeColorAndOpacity[3] !== colors[start + 3]
        ) {
          fillRepeatedPattern(edgeColorAndOpacity, colors, start, count);
          needsUpdate = true;
        }
        if (update && needsUpdate && !colorAttribute.needsUpdate) {
          colorAttribute.needsUpdate = true;
          colorAttribute.version++;
        }
      }
    }
  }

  public *positions() {
    for (const partialEdge of this.partialEdges) {
      yield* partialEdge.positions();
    }
  }

  public translate(v: Vector3) {
    if (this.activeMetaData.center) {
      this.activeMetaData.center.add(v);
    }
    this.boundBox.translate(v);
  }

  public raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    if (this.partialEdges.length > 1) {
      //Always check the bounding sphere first for performance reasons. Most of the time there is no intersection
      _sphere.copy(this.boundSphere);
      _sphere.applyMatrix4(this.matrixWorld);
      _sphere.radius += raycaster.params.Line?.threshold ?? 0;
      if (!raycaster.ray.intersectsSphere(_sphere)) {
        return;
      }
    }
    const partialIntersects: Intersection[] = [];
    for (const partialEdge of this.partialEdges) {
      partialEdge.raycast(raycaster, partialIntersects);
    }
    for (const partialIntersect of partialIntersects) {
      const { object, ...tail } = partialIntersect;
      intersects.push({ ...tail, object: this });
    }
  }
}
